import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { BookingList } from "../../../components/dashboard/bookings/booking-list";
import { getAllBookings, getAllGlamPackages } from "../../../services/api";
import { toHalifaxDateString } from "../../../lib/timezone";
import { toast } from "react-toastify";
import { cn } from "../../../lib/util";
import { ToastProvider } from "../../../components/providers/toast-provider";

interface Booking {
  booking_start_date_time: string;
  id: string;
  booking_name: string;
  name?: string;
  category: string;
  glam_spot: string;
  number_of_faces: number;
  date: string;
  time: string;
  status: string;
}

type Filter = "today" | "upcoming" | "past" | "all";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "today", label: "Today" },
  { id: "upcoming", label: "Upcoming" },
  { id: "past", label: "Past" },
  { id: "all", label: "All" },
];

export default function BookingsPage() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>("today");
  const [query, setQuery] = useState("");

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setIsLoading(true);
      const [bookingsResponse, packagesResponse] = (await Promise.all([
        getAllBookings(),
        getAllGlamPackages(),
      ])) as any;

      const packages = packagesResponse.data.data;
      const formatted = bookingsResponse.data.data.map((booking: any) => {
        const glamPackage = packages.find(
          (pkg: any) =>
            pkg.id.toString().trim() === booking.glam_package_id?.toString().trim()
        );
        return {
          ...booking,
          booking_name: booking.booking_name || booking.name,
          date: new Date(booking.booking_start_date_time).toLocaleDateString(),
          time: new Date(booking.booking_start_date_time).toLocaleTimeString(),
          category: glamPackage ? glamPackage.title : "Unknown Package",
        };
      });
      setBookings(formatted);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to fetch data. Please try again.", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const todayIso = toHalifaxDateString(new Date());

  // Counts per filter so each tab advertises how much it holds.
  const counts = useMemo(() => {
    const c = { today: 0, upcoming: 0, past: 0, all: bookings.length };
    bookings.forEach((b) => {
      if (!b.booking_start_date_time) return;
      const iso = toHalifaxDateString(b.booking_start_date_time);
      if (iso === todayIso) c.today += 1;
      else if (iso > todayIso) c.upcoming += 1;
      else c.past += 1;
    });
    return c;
  }, [bookings, todayIso]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bookings.filter((b) => {
      if (q && !`${b.booking_name ?? ""}`.toLowerCase().includes(q)) return false;
      if (!b.booking_start_date_time) return filter === "all";
      const iso = toHalifaxDateString(b.booking_start_date_time);
      if (filter === "today") return iso === todayIso;
      if (filter === "upcoming") return iso > todayIso;
      if (filter === "past") return iso < todayIso;
      return true;
    });
  }, [bookings, filter, query, todayIso]);

  const handleBookingClick = (bookingId: string) => {
    navigate(`/control/bookings/view/${bookingId}`);
  };

  return (
    <div className="mx-auto max-w-6xl">
      <ToastProvider />
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#B87775]">Operations</p>
          <h1 className="font-serif text-3xl text-[#3a2e2c]">Bookings</h1>
          <p className="mt-1 text-sm text-[#8a7a78]">Track and organize every client booking.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/control/bookings/add")}
          className="flex h-11 items-center gap-2 rounded-full bg-[#B87775] px-5 text-sm font-medium text-white transition hover:bg-[#a96764] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2"
        >
          <Plus className="h-4 w-4" />
          Add booking
        </button>
      </div>

      {/* Filters + search */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div
          role="tablist"
          aria-label="Filter bookings"
          className="flex gap-1 rounded-full border border-[#EAD7D6] bg-white p-1"
        >
          {FILTERS.map((f) => {
            const selected = filter === f.id;
            return (
              <button
                key={f.id}
                role="tab"
                aria-selected={selected}
                onClick={() => setFilter(f.id)}
                className={cn(
                  "flex min-h-[40px] items-center gap-2 rounded-full px-4 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]",
                  selected ? "bg-[#B87775] text-white" : "text-[#8a5a58] hover:bg-[#FBEFEF]"
                )}
              >
                {f.label}
                <span
                  className={cn(
                    "rounded-full px-1.5 text-xs tabular-nums",
                    selected ? "bg-white/25" : "bg-[#FBEFEF] text-[#B87775]"
                  )}
                >
                  {counts[f.id]}
                </span>
              </button>
            );
          })}
        </div>

        <div className="relative ml-auto min-w-[200px] flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9A6B69]" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name"
            className="h-11 w-full rounded-full border border-[#EAD7D6] bg-white pl-10 pr-4 text-sm text-[#3a2e2c] placeholder:text-[#9A6B69] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
          />
        </div>
      </div>

      {!isLoading && filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E3C9C8] bg-white px-6 py-16 text-center">
          <p className="font-serif text-xl text-[#3a2e2c]">
            {filter === "today" ? "Nothing booked today" : "No bookings here"}
          </p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-[#8a7a78]">
            {filter === "today"
              ? "When a client books today, it'll show up here. You can also add one manually."
              : "Try another filter, or add a booking to get started."}
          </p>
          <button
            type="button"
            onClick={() => navigate("/control/bookings/add")}
            className="mt-4 rounded-full bg-[#B87775] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#a96764]"
          >
            Add booking
          </button>
        </div>
      ) : (
        <BookingList
          bookings={filtered as any}
          isLoading={isLoading}
          onBookingClick={handleBookingClick}
          glamPackages={[]}
        />
      )}
    </div>
  );
}
